import type {
    Project, Graph, Node, Connection, GraphResponse, SaveConnectionPayload,
    Asset, AssetFolder, Entity, Variable, VariableSet, Template, TemplateProperty,
    ExportJob, ImportJob, ProjectStats,
} from './types';

const BASE_URL = '/api';

class ApiClient {
    private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
        const response = await fetch(`${BASE_URL}${path}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `API error: ${response.status}`);
        }

        // 204 No Content
        if (response.status === 204) {
            return {} as T;
        }

        return response.json();
    }

    // Projects
    async listProjects(): Promise<Project[]> {
        return this.request('GET', '/projects');
    }

    async createProject(data: { name: string; slug: string; description?: string }): Promise<{ project: Project; graphs: Graph[]; rootGraph: Graph }> {
        return this.request('POST', '/projects', data);
    }

    async getProject(projectUuid: string): Promise<{ project: Project; graphs: Graph[]; rootGraph: Graph }> {
        return this.request('GET', `/projects/${projectUuid}`);
    }

    async updateProject(projectUuid: string, data: Partial<Project>): Promise<Project> {
        return this.request('PATCH', `/projects/${projectUuid}`, data);
    }

    async deleteProject(projectUuid: string): Promise<void> {
        return this.request('DELETE', `/projects/${projectUuid}`);
    }

    // Graphs
    async getGraph(graphUuid: string): Promise<GraphResponse> {
        return this.request('GET', `/graphs/${graphUuid}`);
    }

    async updateGraph(graphUuid: string, data: Partial<Graph>): Promise<Graph> {
        return this.request('PATCH', `/graphs/${graphUuid}`, data);
    }

    async saveGraphContent(
        graphUuid: string,
        data: { nodes?: Node[]; connections?: Connection[] }
    ): Promise<{ message: string; graph: Graph; skipped_deletions: string[] }> {
        return this.request('PUT', `/graphs/${graphUuid}/content`, data);
    }

    // Nodes
    async createNode(graphUuid: string, nodeData: Partial<Node>): Promise<Node> {
        return this.request('POST', `/graphs/${graphUuid}/nodes`, nodeData);
    }

    async updateNode(nodeUuid: string, data: Partial<Node>): Promise<Node> {
        return this.request('PATCH', `/nodes/${nodeUuid}`, data);
    }

    async deleteNode(nodeUuid: string): Promise<void> {
        return this.request('DELETE', `/nodes/${nodeUuid}`);
    }

    // Connections
    async createConnection(graphUuid: string, connData: SaveConnectionPayload): Promise<Connection> {
        return this.request('POST', `/graphs/${graphUuid}/connections`, connData);
    }

    async deleteConnection(connectionUuid: string): Promise<void> {
        return this.request('DELETE', `/connections/${connectionUuid}`);
    }

    // Assets
    async listAssets(projectUuid: string, params?: { folder_id?: string; kind?: string; search?: string }): Promise<Asset[]> {
        const query = new URLSearchParams();
        if (params?.folder_id) query.append('folder_id', params.folder_id);
        if (params?.kind) query.append('kind', params.kind);
        if (params?.search) query.append('search', params.search);
        const queryStr = query.toString();
        return this.request('GET', `/projects/${projectUuid}/assets${queryStr ? '?' + queryStr : ''}`);
    }

    async uploadAsset(projectUuid: string, file: File, folderId?: string): Promise<Asset> {
        const formData = new FormData();
        formData.append('file', file);
        if (folderId) formData.append('folder_id', folderId);
        formData.append('name', file.name.replace(/\.[^.]+$/, ''));

        const response = await fetch(`${BASE_URL}/projects/${projectUuid}/assets`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Upload failed: ${response.status}`);
        }

        return response.json();
    }

    async updateAsset(projectUuid: string, assetUuid: string, data: Partial<Asset>): Promise<Asset> {
        return this.request('PATCH', `/projects/${projectUuid}/assets/${assetUuid}`, data);
    }

    async deleteAsset(projectUuid: string, assetUuid: string): Promise<void> {
        return this.request('DELETE', `/projects/${projectUuid}/assets/${assetUuid}`);
    }

    // Entities
    async listEntities(projectUuid: string): Promise<Entity[]> {
        return this.request('GET', `/projects/${projectUuid}/entities`);
    }

    async createEntity(projectUuid: string, data: Partial<Entity>): Promise<Entity> {
        return this.request('POST', `/projects/${projectUuid}/entities`, data);
    }

    async updateEntity(projectUuid: string, entityUuid: string, data: Partial<Entity>): Promise<Entity> {
        return this.request('PATCH', `/projects/${projectUuid}/entities/${entityUuid}`, data);
    }

    async deleteEntity(projectUuid: string, entityUuid: string): Promise<void> {
        return this.request('DELETE', `/projects/${projectUuid}/entities/${entityUuid}`);
    }

    // Variable Sets & Variables
    async listVariableSets(projectUuid: string): Promise<VariableSet[]> {
        return this.request('GET', `/projects/${projectUuid}/variable-sets`);
    }

    async createVariableSet(projectUuid: string, data: { name: string }): Promise<VariableSet> {
        return this.request('POST', `/projects/${projectUuid}/variable-sets`, data);
    }

    async updateVariableSet(projectUuid: string, setUuid: string, data: { name: string }): Promise<VariableSet> {
        return this.request('PATCH', `/projects/${projectUuid}/variable-sets/${setUuid}`, data);
    }

    async deleteVariableSet(projectUuid: string, setUuid: string): Promise<void> {
        return this.request('DELETE', `/projects/${projectUuid}/variable-sets/${setUuid}`);
    }

    async createVariable(projectUuid: string, setUuid: string, data: Partial<Variable>): Promise<Variable> {
        return this.request('POST', `/projects/${projectUuid}/variable-sets/${setUuid}/variables`, data);
    }

    async updateVariable(variableUuid: string, data: Partial<Variable>): Promise<Variable> {
        return this.request('PATCH', `/variables/${variableUuid}`, data);
    }

    async deleteVariable(variableUuid: string): Promise<void> {
        return this.request('DELETE', `/variables/${variableUuid}`);
    }

    // Templates
    async listTemplates(projectUuid: string, appliesTo?: string): Promise<Template[]> {
        const path = appliesTo ? `/projects/${projectUuid}/templates?applies_to=${appliesTo}` : `/projects/${projectUuid}/templates`;
        return this.request('GET', path);
    }

    async createTemplate(projectUuid: string, data: Partial<Template>): Promise<Template> {
        return this.request('POST', `/projects/${projectUuid}/templates`, data);
    }

    async updateTemplate(projectUuid: string, templateUuid: string, data: Partial<Template>): Promise<Template> {
        return this.request('PATCH', `/projects/${projectUuid}/templates/${templateUuid}`, data);
    }

    async deleteTemplate(projectUuid: string, templateUuid: string): Promise<void> {
        return this.request('DELETE', `/projects/${projectUuid}/templates/${templateUuid}`);
    }

    /**
     * Create a child graph for a container node
     *
     * Lazily initializes a new child graph seeded with Entry/Exit nodes for
     * pin pass-through. Called when opening a container for the first time.
     *
     * The response includes the newly created child graph with its Entry/Exit
     * nodes already placed and configured.
     *
     * @param {string} nodeUuid - UUID of the container node
     * @returns {Promise<Graph>} The newly created child graph
     *
     * @example
     * // User double-clicks a container node
     * const childGraph = await apiClient.createChildGraph(containerNodeUuid);
     * await openGraph(childGraph.uuid);
     */
    async createChildGraph(nodeUuid: string): Promise<Graph> {
        return this.request('POST', `/nodes/${nodeUuid}/child-graph`, {});
    }

    /**
     * Save viewport state for a graph
     *
     * Persists the user's camera position (pan/zoom) so that re-entering
     * a graph restores the exact view they were working in.
     *
     * Viewport state includes:
     * - x, y: pan position (can be negative)
     * - zoom: magnification level (e.g., 1.0 = 100%, 2.0 = 200%)
     *
     * Called automatically by the EditorShell whenever the user stops panning/zooming.
     *
     * @param {string} graphUuid - UUID of the graph
     * @param {Object} viewport - Camera position {x, y, zoom}
     * @returns {Promise<Graph>} Updated graph with new viewport
     *
     * @example
     * // Save viewport when user changes camera
     * await apiClient.saveViewport(graphUuid, {
     *   x: -150.5,
     *   y: 280.3,
     *   zoom: 1.2,
     * });
     */
    async saveViewport(
        graphUuid: string,
        viewport: { x: number; y: number; zoom: number }
    ): Promise<Graph> {
        return this.request('PATCH', `/graphs/${graphUuid}`, { viewport });
    }

    /**
     * Get breadcrumb path from root to this graph
     *
     * Returns the ancestry chain showing nesting hierarchy. Used by the
     * GraphLocation component (parent lookup) and search results to display context.
     *
     * **Response Example:**
     * ```json
     * [
     *   { "uuid": "...", "name": "Root" },
     *   { "uuid": "...", "name": "Chapter 1" },
     *   { "uuid": "...", "name": "Tavern Scene" }
     * ]
     * ```
     *
     * This is already included in GET /graphs/{uuid} response, but exposed
     * separately for convenience.
     *
     * @param {string} graphUuid - UUID of the graph
     * @returns {Promise<Array>} Breadcrumb path
     */
    async getBreadcrumb(graphUuid: string): Promise<Array<{ uuid: string; name: string }>> {
        const response = await this.request('GET', `/graphs/${graphUuid}`);
        return response.breadcrumb || [];
    }

    // Export (doc 09 §2) — canonical JSON + .dgpack bundles, queued jobs polled via getExport()
    async exportProjectJson(projectUuid: string): Promise<ExportJob> {
        return this.request('POST', `/projects/${projectUuid}/export`, {});
    }

    async exportProjectBundle(projectUuid: string): Promise<ExportJob> {
        return this.request('POST', `/projects/${projectUuid}/export/bundle`, {});
    }

    async exportNodeBundle(nodeUuid: string): Promise<ExportJob> {
        return this.request('POST', `/nodes/${nodeUuid}/export/bundle`, {});
    }

    async getExport(exportUuid: string): Promise<ExportJob> {
        return this.request('GET', `/exports/${exportUuid}`);
    }

    // Import (doc 09 §3-4) — .dgpack bundles, queued jobs polled via getImport()
    async importProjectBundle(
        file: File,
        mode: 'new' | 'restore',
        options?: { dryRun?: boolean; projectUuid?: string },
    ): Promise<ImportJob> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', mode);
        if (options?.dryRun) formData.append('dry_run', '1');
        if (options?.projectUuid) formData.append('project_uuid', options.projectUuid);

        const response = await fetch(`${BASE_URL}/projects/import/bundle`, { method: 'POST', body: formData });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Import failed: ${response.status}`);
        }
        return response.json();
    }

    async importGraphBundle(graphUuid: string, file: File, dryRun?: boolean): Promise<ImportJob> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', 'subtree');
        if (dryRun) formData.append('dry_run', '1');

        const response = await fetch(`${BASE_URL}/graphs/${graphUuid}/import/bundle`, { method: 'POST', body: formData });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Import failed: ${response.status}`);
        }
        return response.json();
    }

    async getImport(importUuid: string): Promise<ImportJob> {
        return this.request('GET', `/imports/${importUuid}`);
    }

    // Localization CSV/XLSX round-trip (doc 06 §10)
    async exportTexts(projectUuid: string, format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
        const response = await fetch(`${BASE_URL}/projects/${projectUuid}/texts/export?format=${format}`);
        if (!response.ok) {
            throw new Error(`Texts export failed: ${response.status}`);
        }
        return response.blob();
    }

    /** Accepts a .csv or .xlsx file — the backend distinguishes by file extension. */
    async importTexts(projectUuid: string, file: File): Promise<{ imported: number; skipped: number; errors: string[] }> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BASE_URL}/projects/${projectUuid}/texts/import`, { method: 'POST', body: formData });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Texts import failed: ${response.status}`);
        }
        return response.json();
    }

    // Reports & statistics (doc 06 §11)
    async getStats(projectUuid: string): Promise<ProjectStats> {
        return this.request('GET', `/projects/${projectUuid}/stats`);
    }
}

export const apiClient = new ApiClient();
